/**
 * PlaceCoreGalleryStrip — Max 4 thumbnails below hero.
 * Shows 3 + "+X bilder" overlay if more exist. Tap opens lightbox.
 * Thumbnails use 320-variant; lightbox uses 640-variant.
 * No horizontal scroll, no pagination, no "Galleri" label.
 *
 * Supports syncing with hero carousel via activeIndex + onSelectIndex.
 */
import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  thumbUrls: string[];
  fullUrls: string[];
  /** Currently active hero image index (for highlight sync). */
  activeHeroIndex?: number;
  /** Called when user taps a thumbnail to jump hero to that image. */
  onSelectIndex?: (index: number) => void;
}

export function PlaceCoreGalleryStrip({ thumbUrls, fullUrls, activeHeroIndex, onSelectIndex }: Props) {
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);

  if (thumbUrls.length < 2) return null;

  const totalCount = thumbUrls.length;
  const showOverlay = totalCount > 4;
  const visibleThumbs = showOverlay ? thumbUrls.slice(0, 3) : thumbUrls.slice(0, 4);
  const extraCount = totalCount - 3;

  const prev = () =>
    setSelectedIdx((i) => (i !== null ? (i - 1 + totalCount) % totalCount : 0));
  const next = () =>
    setSelectedIdx((i) => (i !== null ? (i + 1) % totalCount : 0));

  const handleThumbClick = (index: number) => {
    if (onSelectIndex) {
      // On mobile: jump the hero carousel instead of opening lightbox
      onSelectIndex(index);
    } else {
      // Fallback: open lightbox (desktop behavior)
      setSelectedIdx(index);
    }
  };

  return (
    <>
      <div className="flex gap-2" data-testid="place-gallery-strip">
        {visibleThumbs.map((url, i) => (
          <button
            key={url}
            data-testid="place-gallery-thumb"
            data-index={i}
            onClick={() => handleThumbClick(i)}
            className={cn(
              "shrink-0 rounded-lg overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-opacity",
              activeHeroIndex !== undefined && activeHeroIndex !== i && "opacity-60",
              activeHeroIndex === i && "ring-2 ring-primary/50",
            )}
          >
            <img
              src={url}
              alt={`Bild ${i + 1}`}
              className="h-16 sm:h-20 w-auto aspect-[4/3] object-cover"
              loading="lazy"
            />
          </button>
        ))}

        {showOverlay && (
          <button
            data-testid="place-gallery-thumb-overlay"
            data-index={3}
            onClick={() => handleThumbClick(3)}
            className={cn(
              "shrink-0 rounded-lg overflow-hidden relative focus:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-opacity",
              activeHeroIndex !== undefined && activeHeroIndex !== 3 && "opacity-60",
              activeHeroIndex === 3 && "ring-2 ring-primary/50",
            )}
          >
            <img
              src={thumbUrls[3]}
              alt={`Bild 4`}
              className="h-16 sm:h-20 w-auto aspect-[4/3] object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <span data-testid="place-gallery-overlay-count" className="text-white text-sm font-medium">+{extraCount} bilder</span>
            </div>
          </button>
        )}
      </div>

      {/* Lightbox — uses 640 variants */}
      <Dialog open={selectedIdx !== null} onOpenChange={() => setSelectedIdx(null)}>
        <DialogContent className="max-w-3xl p-0 bg-black border-none">
          {selectedIdx !== null && (
            <div className="relative flex items-center justify-center min-h-[50vh]">
              <img
                src={fullUrls[selectedIdx] || thumbUrls[selectedIdx]}
                alt={`Bild ${selectedIdx + 1}`}
                className="max-h-[80vh] w-auto max-w-full object-contain"
              />
              {totalCount > 1 && (
                <>
                  <button
                    onClick={prev}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-1.5 hover:bg-black/70"
                    aria-label="Föregående bild"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={next}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full p-1.5 hover:bg-black/70"
                    aria-label="Nästa bild"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
              <span className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/70 text-xs">
                {selectedIdx + 1} / {totalCount}
              </span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
